import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatDate } from '../../shared/utils/dateUtils';
import { api, trackEvent } from '../../shared/utils/api';
import { cleanSubtopicName } from '../../shared/utils/topicDisplay';
import { DEFAULT_STATUSES, getStatusColor } from './TopicDetail';
import { bandHex, bandKey, PROVIDER_COLOR, PROVIDER_INITIAL } from './bandColors';
import QuizPanel from './QuizPanel';

// ── Confidence trend chart (collapsible) ──
function ConfidenceChart({ history, colour }) {
  const vals = (history || []).slice().reverse().filter(h => h.confidence != null);
  if (vals.length < 2) return null;
  const W = 400, H = 58;
  const pts = vals.map((h, i) => ({
    x: (i / (vals.length - 1)) * W,
    y: H - (h.confidence * (H - 4)) - 2,
  }));
  const poly = pts.map(p => `${p.x},${p.y}`).join(' ');
  const area = `M0,${H} ${pts.map(p => `L${p.x},${p.y}`).join(' ')} L${W},${H} Z`;
  const trendDir = vals[vals.length - 1].confidence > vals[0].confidence
    ? 'trending up'
    : vals[vals.length - 1].confidence < vals[0].confidence
    ? 'trending down'
    : 'holding steady';
  const trendArrow = trendDir.startsWith('trending up') ? '↗'
    : trendDir.startsWith('trending down') ? '↘' : '→';
  return (
    <div className="chart-area">
      <span className="chart-meta">{trendArrow} {trendDir}</span>
      <svg className="chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke="#e0ddd6" strokeWidth="0.5" strokeDasharray="2,3" />
        <path d={area} fill={colour} opacity="0.1" />
        <polyline points={poly} fill="none" stroke={colour} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="2.5" fill={colour} />)}
      </svg>
    </div>
  );
}

function ProviderRow({ name, dateText, score }) {
  const initial = PROVIDER_INITIAL[name] || '?';
  const bg = PROVIDER_COLOR[name] || '#9ca3af';
  const band = bandKey(score != null ? score / 100 : null);
  const scoreClass = band === 'weak' ? 'c-r' : band === 'needs-work' ? 'c-a' : band === 'strong' ? 'c-g' : 'c-n';
  return (
    <div className="provider-row">
      <div className="provider-left">
        <div className="provider-icon" style={{ background: bg }}>{initial}</div>
        <span className="provider-name">{name}</span>
        <span className="provider-date">{dateText}</span>
      </div>
      <span className={`provider-score ${scoreClass}`}>
        {score != null ? `${Math.round(score)}%` : '—'}
      </span>
    </div>
  );
}

// Status derivation — unified vocabulary across filter chips, status
// pills, and badges. Red + amber both say "Needs work".
function deriveStatus(topic, explicitStatus) {
  if (explicitStatus) return explicitStatus;
  if (topic.confidence == null) return 'Not started';
  const pct = topic.confidence * 100;
  if (pct <= 60) return 'Needs work';
  return 'Strong';
}

function statusChipClass(label, band) {
  if (label === 'Strong') return 'sc-green';
  if (label === 'Not started') return 'sc-neutral';
  // "Needs work" — colour by underlying band (red at 0-30, amber at 31-60)
  return band === 'weak' ? 'sc-red' : 'sc-amber';
}

/**
 * Topic detail panel (canonical v2 layout).
 *
 * Architectural pattern: the panel itself is the warm-beige substrate
 * (#f5f3ee). Every interactive/info element inside is a white card.
 * The panel connects flush to the selected L3 row above via
 * margin-top: -1px and a shared 1.5px blue border (minus top).
 *
 * Order:
 *   A. Header (title + meta)
 *   B. Scenario blurb — white card
 *   C. Status strip — ONE row, 3 cells (Confidence | Readiness | Status)
 *   D. Primary action row — lifted practice CTA
 *   E. Rate your confidence — single slider (thumb colour follows band)
 *   F. Confidence trend — <details>, collapsible, open by default
 *   G. External scores — <details>, collapsible, open by default
 *   H. Notes — <details>, collapsible, closed by default
 *   I. Footer
 */
export default function TopicDetailPanel({ topic, onSave, statuses, onNavigateToSRA }) {
  const [conf, setConf] = useState(topic.confidence != null ? Math.round(topic.confidence * 100) : 50);
  const [hasConf, setHasConf] = useState(topic.confidence != null);
  const [status, setStatus] = useState(topic.status || '');
  const [statusOverriding, setStatusOverriding] = useState(false);
  const [notes, setNotes] = useState(topic.notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [questionStatus, setQuestionStatus] = useState(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const saveTimer = useRef(null);
  const clearTimer = useRef(null);

  useEffect(() => {
    api(`/api/topics/${topic.id}/diagnostic`).then(setDiagnostic).catch(() => setDiagnostic({}));
    api(`/api/questions/topic/${topic.id}/status`).then(setQuestionStatus).catch(() => setQuestionStatus(null));
  }, [topic.id]);

  const save = useCallback(async (confVal, hasC, statusVal, notesVal) => {
    setSaving(true);
    const confNum = hasC ? Math.min(100, Math.max(0, parseInt(confVal))) / 100 : null;
    const wasUnset = !topic.confidence && topic.confidence !== 0;
    await api(`/api/topics/${topic.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        confidence: (isNaN(confNum) || confNum === null) ? null : confNum,
        status: statusVal,
        notes: notesVal,
        date_updated: new Date().toLocaleDateString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        }).replace(/\//g, '.'),
        is_estimated: false,
      }),
    });
    if (hasC && wasUnset) trackEvent('first_confidence_set');
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
    onSave && onSave(topic.id, {
      confidence: (isNaN(confNum) || confNum === null) ? null : confNum,
      status: statusVal,
      notes: notesVal,
      is_estimated: false,
    });
  }, [topic.id, topic.confidence, onSave]);

  const autoSave = (cVal, hasC, sVal, nVal) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save(cVal, hasC, sVal, nVal), 600);
  };

  const startClearConfidence = () => {
    setConfirmClear(true);
    clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => setConfirmClear(false), 3000);
  };
  const confirmClearConfidence = async () => {
    clearTimeout(clearTimer.current);
    setConfirmClear(false);
    await api(`/api/topics/${topic.id}/clear-confidence`, { method: 'POST' });
    setHasConf(false);
    setConf(50);
    onSave && onSave(topic.id, { confidence: null });
  };
  const cancelClear = () => {
    clearTimeout(clearTimer.current);
    setConfirmClear(false);
  };

  const handleOpenQuiz = (e) => { e?.stopPropagation?.(); setQuizOpen(true); };
  const handleCloseQuiz = () => setQuizOpen(false);
  const handleAllAnswered = async () => {
    handleCloseQuiz();
    const data = await api(`/api/topics/${topic.id}/attempt-complete`, { method: 'POST' });
    const updated = Array.isArray(data) ? data.find(t => t.id === topic.id) : null;
    if (updated) onSave && onSave(topic.id, { confidence: updated.confidence });
    return updated?.confidence ?? null;
  };

  // Derived values
  const scenario = cleanSubtopicName(topic.sub_subtopic || topic.detail || '');
  const confPct = topic.confidence != null ? Math.round(topic.confidence * 100) : null;
  const confColourHex = bandHex(topic.confidence);
  const confBand = bandKey(topic.confidence);
  const confClass = confBand === 'weak' ? 'c-r' : confBand === 'needs-work' ? 'c-a' : confBand === 'strong' ? 'c-g' : 'c-n';

  const readiness = (topic.decayed_confidence != null && !topic.is_insufficient_data)
    ? topic.decayed_confidence : null;
  const readinessBand = bandKey(readiness);
  const readClass = readinessBand === 'weak' ? 'c-r' : readinessBand === 'needs-work' ? 'c-a' : readinessBand === 'strong' ? 'c-g' : 'c-n';

  const derivedStatus = deriveStatus(topic, status);
  const statusIsOverride = !!status;
  const chipClass = statusChipClass(derivedStatus, confBand);

  // Slider band class for thumb colour
  const sliderBand = conf <= 30 ? 'band-red' : conf <= 60 ? 'band-amber' : 'band-green';

  // External scores
  const providerBreakdown = diagnostic?.provider_breakdown || {};
  const barbri = providerBreakdown.BARBRI || providerBreakdown.Barbri;
  const lawDrills = providerBreakdown['Law Drills'];
  const sqetrackAttempts = (diagnostic?.mcq_stats?.attempted) || 0;
  const sqetrackCorrect = (diagnostic?.mcq_stats?.correct) || 0;
  const sqetrackAccuracy = diagnostic?.mcq_stats?.accuracy ?? null;
  const externalSources = [
    barbri && barbri.questions > 0 && { name: 'BARBRI', dateText: `${barbri.questions} question${barbri.questions !== 1 ? 's' : ''}`, score: barbri.accuracy },
    lawDrills && lawDrills.questions > 0 && { name: 'Law Drills', dateText: `${lawDrills.questions} question${lawDrills.questions !== 1 ? 's' : ''}`, score: lawDrills.accuracy },
    sqetrackAttempts > 0 && { name: 'SQETrack', dateText: `${sqetrackCorrect} of ${sqetrackAttempts} correct`, score: sqetrackAccuracy },
  ].filter(Boolean);
  const hasExternal = externalSources.length > 0;

  // Practice — combine questionStatus + MCQ stats
  const practiceAvailable = questionStatus?.available ?? 0;
  const practiceCompleted = questionStatus?.completed ?? 0;
  const practiceCorrect = questionStatus?.correct ?? sqetrackCorrect;
  const practiceTotal = practiceAvailable + practiceCompleted;
  const practicePct = practiceCompleted > 0
    ? Math.round((practiceCorrect / practiceCompleted) * 100) : null;
  const practiceHasData = practiceTotal > 0;

  const confidenceHistory = diagnostic?.confidence_history;
  const historyCount = (confidenceHistory || []).filter(h => h.confidence != null).length;
  const hasChart = historyCount >= 2;

  const hasNote = !!(notes && notes.trim().length > 0);

  const statusOptions = (statuses || DEFAULT_STATUSES).map(s => typeof s === 'string' ? s : s.label);

  return (
    <div className="detail-panel">
      {quizOpen && (
        <QuizPanel
          topicId={topic.id}
          oldConfidence={topic.confidence}
          onClose={handleCloseQuiz}
          onAllAnswered={handleAllAnswered}
        />
      )}

      {/* A. Header */}
      <div className="detail-header">
        <div className="detail-title">{cleanSubtopicName(topic.display_name || topic.subtopic || '')}</div>
        <div className="detail-meta">
          {topic.principle_count > 0 && <span>{topic.principle_count} principles</span>}
          {topic.principle_count > 0 && <span className="meta-dot" />}
          {topic.main_topic && <span>{topic.main_topic}</span>}
          {topic.main_topic && <span className="meta-dot" />}
          {topic.module && <span>{topic.module}</span>}
        </div>
      </div>

      <div className="detail-body">

        {/* B. Scenario blurb (white card on warm substrate) */}
        {scenario && <div className="scenario-blurb">{scenario}</div>}

        {/* C. Status strip — ONE row, three cells */}
        <div className="status-strip">
          <div className="status-cell">
            <div className="status-cell-label">Confidence</div>
            <div className={`status-cell-value ${confClass}`}>
              {confPct !== null ? `${confPct}%` : '—'}
            </div>
          </div>
          <div className="status-cell">
            <div className="status-cell-label">
              Readiness <span className="help-icon" title="Blended from confidence, MCQ accuracy, and recency.">?</span>
            </div>
            <div className={`status-cell-value ${readClass}`}>
              {readiness !== null ? `${Math.round(readiness * 100)}%` : '—'}
            </div>
          </div>
          <div className="status-cell status-pill-cell">
            <div className="status-cell-label">Status</div>
            {!statusOverriding ? (
              <button
                className={`status-chip ${chipClass}`}
                onClick={() => setStatusOverriding(true)}
              >
                {derivedStatus}
                <svg width="9" height="9" viewBox="0 0 9 9" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M2 3.5l2.5 2.5L7 3.5" /></svg>
              </button>
            ) : (
              <select
                className="status-select-inline"
                value={status}
                style={status ? { background: (getStatusColor(status, statuses) || '#F3F4F6') + '80' } : {}}
                onChange={e => {
                  setStatus(e.target.value);
                  setStatusOverriding(false);
                  autoSave(conf, hasConf, e.target.value, notes);
                }}
                onBlur={() => setStatusOverriding(false)}
                autoFocus
              >
                <option value="">— auto —</option>
                {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            <div className="status-source">
              {statusIsOverride ? 'overridden' : 'auto'} ·{' '}
              {statusIsOverride ? (
                <button className="link-btn" onClick={() => { setStatus(''); autoSave(conf, hasConf, '', notes); }}>reset to auto</button>
              ) : (
                <button className="link-btn" onClick={() => setStatusOverriding(true)}>override</button>
              )}
            </div>
          </div>
        </div>

        {/* D. Primary action — lifted above the fold */}
        {practiceHasData ? (
          <div className="primary-action-row">
            <div className="primary-action-info">
              <span className="primary-action-count">
                {practiceTotal} practice question{practiceTotal !== 1 ? 's' : ''}
                {practiceCompleted > 0 && practicePct !== null && ` · ${practiceCorrect} of ${practiceCompleted} correct (${practicePct}%)`}
              </span>
              <span className="primary-action-sub">
                {practiceCompleted} attempted · {practiceAvailable} unseen
              </span>
            </div>
            {practiceAvailable > 0 && (
              <button className="practice-btn" onClick={handleOpenQuiz}>
                Practice this topic
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 4l4 4-4 4" /></svg>
              </button>
            )}
          </div>
        ) : (
          <div className="primary-action-row" style={{ justifyContent: 'flex-start' }}>
            <div className="primary-action-info">
              <span className="primary-action-count">Practice questions coming soon for this topic</span>
              <span className="primary-action-sub">Check back after the next bank update.</span>
            </div>
          </div>
        )}

        {/* E. Rate your confidence */}
        <div className="conf-section">
          <div className="section-label">Rate your confidence</div>
          {!hasConf ? (
            <button
              className="set-conf-btn"
              onClick={() => { setHasConf(true); autoSave(50, true, status, notes); }}
            >
              Set confidence
            </button>
          ) : (
            <>
              <div className="slider-row">
                <input
                  type="range" min="0" max="100" value={conf}
                  className={`conf-slider ${sliderBand}`}
                  style={{
                    background: `linear-gradient(to right, ${bandHex(conf / 100)} ${conf}%, var(--border-soft) ${conf}%)`,
                  }}
                  onChange={e => { setConf(parseInt(e.target.value)); autoSave(e.target.value, true, status, notes); }}
                />
                <span className={`conf-val ${confClass}`}>{conf}%</span>
              </div>
              {!confirmClear ? (
                <button className="clear-rating" onClick={startClearConfidence}>Clear rating</button>
              ) : (
                <span className="clear-confirm-inline">
                  <span>Clear?</span>
                  <button className="clear-confirm-yes" onClick={confirmClearConfidence}>Yes</button>
                  <button className="clear-confirm-no" onClick={cancelClear}>No</button>
                </span>
              )}
            </>
          )}
        </div>

        {/* F. Confidence trend */}
        {hasChart && (
          <details className="detail-section" open>
            <summary>
              <span className="summary-label">Confidence trend</span>
              <span className="summary-meta">{historyCount} rating{historyCount === 1 ? '' : 's'}</span>
              <span className="summary-chevron"></span>
            </summary>
            <div className="detail-section-body">
              <ConfidenceChart history={confidenceHistory} colour={confColourHex} />
            </div>
          </details>
        )}

        {/* G. External scores */}
        {hasExternal && (
          <details className="detail-section" open>
            <summary>
              <span className="summary-label">External scores</span>
              <span className="summary-meta">{externalSources.length} source{externalSources.length === 1 ? '' : 's'}</span>
              <span className="summary-chevron"></span>
            </summary>
            <div className="detail-section-body">
              {externalSources.map(s => (
                <ProviderRow key={s.name} name={s.name} dateText={s.dateText} score={s.score} />
              ))}
            </div>
          </details>
        )}

        {/* H. Notes (closed by default unless there's a note) */}
        <details className="detail-section" open={hasNote}>
          <summary>
            <span className="summary-label">Notes</span>
            <span className="summary-meta">{hasNote ? '1 note' : 'none yet'}</span>
            <span className="summary-chevron"></span>
          </summary>
          <div className="detail-section-body">
            <textarea
              className="notes-input"
              rows={2}
              placeholder="Add a note about this topic..."
              value={notes}
              onChange={e => { setNotes(e.target.value); autoSave(conf, hasConf, status, e.target.value); }}
            />
          </div>
        </details>

      </div>

      {/* I. Footer */}
      <div className="detail-footer">
        <span className="detail-footer-left">
          {saving && <span className="save-saving">Saving</span>}
          {saved && !saving && <span className="save-done">Saved</span>}
          {!saving && !saved && topic.date_updated && (
            <span className="save-date">Updated {formatDate(topic.date_updated, 'relative')}</span>
          )}
        </span>
        <span className="detail-footer-right">Topic ID: {topic.id}</span>
      </div>
    </div>
  );
}
